import { useCallback, useState } from 'react';
import { showToast } from '@attentive-platform/stem-ui';

import { FormContainer } from '../../styled';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { USER_API_TOKEN_DETAIL, USER_API_TOKEN_SHARE_TOKEN, USER_API_TOKENS } from '../../urls';

import ApiKeyTokensTable from './components/ApiKeyTokensTable';
import ApiKeySearchSection from './components/ApiKeySearchSection';
import ApiKeyGenerateSection from './components/ApiKeyGenerateSection';

const copyText = async text => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  } catch {
    showToast('Could not copy', 'error');
  }
};

export const ApiKeyManager = () => {
  const [email, setEmail] = useState('');
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState('');
  const [sharingId, setSharingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [searchedEmail, setSearchedEmail] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  const loadTokens = useCallback(async userEmail => {
    const { data, error } = await fetchApiData({
      endpoint: USER_API_TOKENS,
      apiRequest: 'GET',
      params: { email: userEmail }
    });
    if (error) {
      showToast(typeof error === 'string' ? error : 'Failed to load tokens', 'error');
      setTokens([]);
      return;
    }
    setTokens(Array.isArray(data) ? data : []);
  }, []);

  const handleSearch = async () => {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      showToast('Enter a valid email', 'error');
      return;
    }
    setSearchLoading(true);
    setNewToken('');
    setSearchedEmail('');
    const { data, error } = await fetchApiData({
      endpoint: USER_API_TOKENS,
      apiRequest: 'GET',
      params: { email: trimmed }
    });
    setSearchLoading(false);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Search failed', 'error');
      return;
    }
    setSearchedEmail(trimmed);
    setTokens(Array.isArray(data) ? data : []);
  };

  const handleGenerate = async () => {
    if (!searchedEmail) return;
    const name = `Token ${Date.now()}`;
    setGenerateLoading(true);
    const { data, error } = await fetchApiData({
      endpoint: USER_API_TOKENS,
      apiRequest: 'POST',
      data: { email: searchedEmail, name }
    });
    setGenerateLoading(false);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Could not generate token', 'error');
      return;
    }
    setNewToken(data.token);
    showToast('New token generated', 'success');
    await loadTokens(searchedEmail);
  };

  const handleDelete = async id => {
    setDeletingId(id);
    const { error } = await fetchApiData({
      endpoint: interpolate(USER_API_TOKEN_DETAIL, [id]),
      apiRequest: 'DELETE'
    });
    setDeletingId(null);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Delete failed', 'error');
      return;
    }
    showToast('Token removed', 'success');
    await loadTokens(searchedEmail);
  };

  const handleShare = async row => {
    const id = row.id;
    const name = row.name?.trim() || 'API token';
    setSharingId(id);
    const { error } = await fetchApiData({
      endpoint: interpolate(USER_API_TOKEN_SHARE_TOKEN, [id]),
      apiRequest: 'POST',
      data: { email: searchedEmail, name }
    });
    setSharingId(null);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Could not share token', 'error');
      return;
    }
    showToast('Token shared with user', 'success');
  };

  const handleCopy = async tokenToCopy => {
    const text = tokenToCopy || newToken;
    if (text) copyText(text);
    else showToast('Nothing to copy yet', 'error');
  };

  return (
    <FormContainer>
      <ApiKeySearchSection email={email} setEmail={setEmail} onSearch={handleSearch} searchLoading={searchLoading} />

      {searchedEmail && (
        <>
          <ApiKeyGenerateSection
            newToken={newToken}
            handleCopy={handleCopy}
            onGenerate={handleGenerate}
            generateLoading={generateLoading}
          />
          <ApiKeyTokensTable
            tokens={tokens}
            onShare={handleShare}
            deletingId={deletingId}
            sharingId={sharingId}
            handleCopy={handleCopy}
            onDelete={handleDelete}
          />
        </>
      )}
    </FormContainer>
  );
};

export default ApiKeyManager;
